import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ShieldCheck, UserCheck, LayoutList, Megaphone, HeartHandshake, ArrowRight, Quote, Star } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Reveal, Stagger, item, SectionHeading } from "@/components/Reveal";
import { ServiceGrid } from "@/components/ServiceGrid";
import { FaqAccordion } from "@/components/FaqAccordion";
import { CtaBanner } from "@/components/Sections";
import { FounderAvatar } from "@/components/FounderAvatar";

const BENEFIT_ICONS = [Clock, ShieldCheck, UserCheck, LayoutList, Megaphone, HeartHandshake];

export default function HomePage() {
  const { t, to } = useLang();
  const h = t.home;
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={h.marquee} />

      <section className="container-x py-20 lg:py-28" id="benefits" data-testid="benefits-section">
        <SectionHeading eyebrow={h.benefits.eyebrow} title={h.benefits.title} sub={h.benefits.sub} id="benefits" />
        <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {h.benefits.items.map((b, i) => {
            const Icon = BENEFIT_ICONS[i];
            const big = i === 0 || i === 3;
            return (
              <motion.div key={b.title} variants={item} className={`card-soft ${big ? "lg:col-span-1" : ""} ${i === 0 ? "bg-navy text-white hover:border-navy" : ""}`} data-testid={`benefit-card-${i + 1}`}>
                <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${i === 0 ? "bg-white/10 text-teal-300" : "bg-teal-50 text-teal-700"}`}><Icon size={22} /></span>
                <h3 className={`mt-6 font-display text-lg font-bold sm:text-xl ${i === 0 ? "text-white" : "text-slate-900"}`}>{b.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${i === 0 ? "text-slate-300" : "text-slate-600"}`}>{b.desc}</p>
              </motion.div>
            );
          })}
        </Stagger>
      </section>

      <section className="bg-white py-20 lg:py-28" id="services" data-testid="services-overview-section">
        <div className="container-x">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading eyebrow={h.services.eyebrow} title={h.services.title} sub={h.services.sub} id="services-overview" />
            <Reveal delay={0.1}><Link to={to("services")} className="btn-secondary" data-testid="services-overview-cta">{h.services.cta} <ArrowRight size={16} /></Link></Reveal>
          </div>
          <div className="mt-14"><ServiceGrid limit={6} /></div>
        </div>
      </section>

      <section className="container-x py-20 lg:py-28" id="founder" data-testid="founder-section">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5"><FounderAvatar /></Reveal>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow={h.founder.eyebrow} title={h.founder.title} id="founder" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">{h.founder.p1}</p>
              <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">{h.founder.p2}</p>
              <blockquote className="mt-8 border-l-4 border-teal-500 pl-5 font-display text-lg font-medium text-slate-900 md:text-xl">{h.founder.quote}</blockquote>
              <Link to={to("about")} className="btn-primary mt-8" data-testid="founder-cta">{h.founder.cta} <ArrowRight size={16} /></Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-navy py-20 text-white lg:py-28" id="process" data-testid="process-section">
        <div className="container-x relative">
          <SectionHeading eyebrow={h.process.eyebrow} title={h.process.title} light id="process" />
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4" stagger={0.1}>
            {h.process.steps.map((s, i) => (
              <motion.div key={s.title} variants={item} className="group bg-navy p-8 transition-colors hover:bg-navy-2" data-testid={`process-step-${i + 1}`}>
                <span className="font-mono text-sm text-teal-300">0{i + 1}</span>
                <h3 className="mt-6 font-display text-xl font-bold">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="container-x py-20 lg:py-28" id="testimonials" data-testid="testimonials-section">
        <SectionHeading eyebrow={h.testimonials.eyebrow} title={h.testimonials.title} id="testimonials" />
        <Stagger className="mt-14 grid gap-5 md:grid-cols-3">
          {h.testimonials.items.map((tm, i) => (
            <motion.figure key={tm.name} variants={item} className="card-soft flex flex-col" data-testid={`testimonial-${i + 1}`}>
              <Quote size={24} className="text-teal-500" />
              <div className="mt-3 flex gap-0.5 text-amber-400">{[...Array(5)].map((_, k) => <Star key={k} size={14} fill="currentColor" />)}</div>
              <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-slate-700 md:text-base">« {tm.quote} »</blockquote>
              <figcaption className="mt-6 border-t border-slate-100 pt-5">
                <p className="font-display font-semibold text-slate-900">{tm.name}</p>
                <p className="text-xs text-slate-500">{tm.role}</p>
              </figcaption>
            </motion.figure>
          ))}
        </Stagger>
      </section>

      <section className="bg-white py-20 lg:py-28" id="faq" data-testid="mini-faq-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow={h.faq.eyebrow} title={h.faq.title} id="mini-faq" />
            <Reveal delay={0.1}><Link to={`${to("contact")}#faq`} className="btn-secondary mt-8" data-testid="mini-faq-cta">{h.faq.cta} <ArrowRight size={16} /></Link></Reveal>
          </div>
          <Reveal className="lg:col-span-8" delay={0.1}><FaqAccordion items={h.faq.items} idPrefix="mini-faq" /></Reveal>
        </div>
      </section>

      <CtaBanner title={h.cta.title} sub={h.cta.sub} btn1={h.cta.btn1} btn2={h.cta.btn2} to1={to("contact")} to2={`${to("contact")}#contact-forms`} id="home-cta" />
    </div>
  );
}
